import { Component, OnInit, inject } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";

import { AlertService } from "../services";
import { BehaviorSubject, NEVER, Observable, throwError } from "rxjs";
import { UserService } from "../services/user.service";
import {
  AdministrationClientProvider,
  DevicePlatform,
  ITenantDto,
  LoginForUnknownTenantDto,
  LoginUnauthorizedDto,
  RestUserExternal,
  ValidationResult,
  AMClientProvider,
  LoginRequest,
  LoginForUnknownTenantV2Command,
  ILoginForUnknownTenantV2Command,
} from "../web-api/web-api";
import { REGEX_EMAIL } from "../helpers/CstValidators";
import { FormControlBase } from "../common-components/form-controls/form-control-base";
import { CstResponsivePopupService } from "../common-components/resp-popup/services/resp-popup.service";
import { catchError, finalize, switchMap, tap } from "rxjs/operators";
import { TermsOfServicePopupComponent } from "./tos-popup-component/tos-popup.component";
import { APP_TITLE_TOKEN } from "../injection-tokens/app-title-token";
import { Claims } from "../constants";
import { CstDialogsService } from "../common-components/dialogs/cst-dialogs.service";
import { CstDialogResult } from "../common-components/dialogs/cst-dialog-enums";
import { NavRoutes } from "../services/cst-navigation.service";
import { ErrorStateMatcher } from "@angular/material/core";
import { CommonComponentsModule } from "../common-components";
import { RouterModule } from "@angular/router";

export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    _form: FormGroupDirective | NgForm | null,
  ): boolean {
    return control && control.invalid;
  }
}
@Component({
  templateUrl: "login.component.html",
  styleUrls: ["login.component.scss"],
  imports: [CommonComponentsModule, ReactiveFormsModule, RouterModule],
})
export class LoginComponent implements OnInit {
  private static readonly NO_LICENSE_ERROR_MESSAGE = $localize`:@@Login_NoLicense_Error_Message:You are missing the licenses to log in to MainHero.`;

  private _fb = inject(FormBuilder);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _userService = inject(UserService);
  private _adminClient = inject(AdministrationClientProvider);
  private _amClient = inject(AMClientProvider);
  private _alertService = inject(AlertService);
  private _popupService = inject(CstResponsivePopupService);
  private _dlgs = inject(CstDialogsService);
  appTitle = inject(APP_TITLE_TOKEN);

  public matcher = new MyErrorStateMatcher();
  public loginForm!: FormGroup;
  public loading$ = new BehaviorSubject<boolean>(false);
  public returnUrl!: string;

  selectedTabKey: "login" | "tenant-selection" = "login";
  availableTenants: ITenantDto[] = [];

  private _state: any;

  public get state(): any {
    if (!this._state) {
      const rawState = localStorage.getItem("LoginUiState");
      this._state = rawState ? JSON.parse(rawState) : {};
    }
    return this._state;
  }
  public get rememberName() {
    return this.state.rememberName;
  }
  public set rememberName(value: boolean) {
    this.state.rememberName = value;
    this._SaveState();
  }

  public get lastUsername() {
    return this.rememberName && this.state.lastUsername
      ? this.state.lastUsername
      : "";
  }
  public set lastUsername(value: string) {
    this.state.lastUsername = this.rememberName ? value : undefined;
    this._SaveState();
  }

  ngOnInit() {
    this.loginForm = this._fb.group({
      username: new FormControl(this.lastUsername, [Validators.required]),
      password: new FormControl("", [Validators.required]),
    });

    // reset login status
    this._userService.Logout();

    // get return url from route parameters or default to '/'
    this.returnUrl = this._route.snapshot.queryParamMap.get("returnUrl") || "/";
    const userNameFromQuery =
      this._route.snapshot.queryParamMap.get("username");
    if (userNameFromQuery) {
      // remove username query param from url without reloading
      this._router.navigate([], {
        relativeTo: this._route,
        queryParams: { username: null },
        queryParamsHandling: "merge",
        replaceUrl: true,
      });
      this.loginForm.patchValue({ username: userNameFromQuery });
    }
  }

  OnSubmit() {
    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }
    this._alertService.Clear();

    const cmd = new LoginForUnknownTenantV2Command();
    cmd.init(this.loginForm.value);
    cmd.platform = DevicePlatform.Browser;

    this.loading$.next(true);
    this.attemptLogin(cmd).subscribe();
  }

  public onTenantSelectionCancel() {
    this.availableTenants = [];
    this.selectedTabKey = "login";
    const ctrlPassword = this.loginForm.controls["password"];
    ctrlPassword.setValue("");
    setTimeout(() => {
      ctrlPassword.markAsPristine();
      ctrlPassword.markAsUntouched();
      ctrlPassword.setErrors(null);
    });
  }

  public onTenantSelected(tenant: ITenantDto) {
    const value = this.loginForm.value;
    const cmd = new LoginForUnknownTenantV2Command({
      username: `${tenant.domain}/${value.username}`,
      password: value.password,
      platform: DevicePlatform.Browser,
    } as ILoginForUnknownTenantV2Command);

    this.loading$.next(true);
    this.attemptLogin(cmd).subscribe();
  }

  private attemptLogin(
    cmd: LoginForUnknownTenantV2Command | LoginRequest,
  ): Observable<any> {
    if (cmd instanceof LoginRequest) {
      return this._amClient.webAPIWebUIFeaturesAuthLoginEndpoint(cmd).pipe(
        finalize(() => this.loading$.next(false)),
        catchError((error: any) => {
          return this.handleErrors$(cmd, error);
        }),
        tap((result: RestUserExternal) => {
          this.handleLoginSuccess(result);
        }),
      );
    }

    return this._amClient.webAPIWebUIFeaturesAuthLoginTenantsEndpoint(cmd).pipe(
      finalize(() => this.loading$.next(false)),
      catchError((error: any) => {
        return this.handleErrors$(cmd, error);
      }),
      tap((result: LoginForUnknownTenantDto) => {
        if (result.authenticatedUser) {
          this.handleLoginSuccess(result.authenticatedUser);
          return;
        }
        this.availableTenants = result.tenants ?? [];
        this.selectedTabKey = "tenant-selection";
      }),
    );
  }

  private handleErrors$(
    cmd: LoginForUnknownTenantV2Command | LoginRequest,
    error: any,
  ) {
    if (error instanceof LoginUnauthorizedDto) {
      return this.handleUnauthorizedLoginErrors(cmd, error);
    } else if (error instanceof ValidationResult) {
      FormControlBase.ApplyValidationResultToForm(this.loginForm, error);
      return NEVER;
    }
    // handle other errors?
    return throwError(() => new Error(error));
  }
  private handleUnauthorizedLoginErrors(
    cmd: LoginForUnknownTenantV2Command | LoginRequest,
    error: any,
  ) {
    if (error?.mustAcceptTermsOfUse) {
      return this.handleToSPopup(cmd, error);
    }
    this._alertService.Error(error.message || "Unknown error during login.");
    return NEVER;
  }

  private handleToSPopup(
    cmd: LoginRequest,
    error: LoginUnauthorizedDto,
  ): Observable<any> {
    const dialogRef = this._popupService.open<
      TermsOfServicePopupComponent,
      LoginUnauthorizedDto,
      boolean
    >(TermsOfServicePopupComponent, { data: error });

    return dialogRef.afterClosed().pipe(
      switchMap((accepted?: boolean) => {
        if (!accepted) {
          return throwError(() => error);
        }

        cmd.acceptsTermsOfUse = true;
        return this.attemptLogin(cmd);
      }),
    );
  }

  private async handleLoginSuccess(result: RestUserExternal) {
    let anyLic = false;
    const rawInternalServices = result.settings?.system?.internalServices;
    const internalServices = rawInternalServices as unknown as
      | Record<string, unknown>
      | undefined;

    for (const prop of Object.keys(internalServices ?? {})) {
      if (!prop.toLowerCase().endsWith("islicensed")) {
        continue;
      }
      if (internalServices?.[prop] === true) {
        anyLic = true;
        break;
      }
    }
    if (!anyLic) {
      this._alertService.Error(LoginComponent.NO_LICENSE_ERROR_MESSAGE);
      return;
    }

    this._userService.Login(result);
    this.lastUsername = result.username ?? "";
    const navigated = await this._router.navigateByUrl(this.returnUrl);
    if (!navigated) {
      this._alertService.Error(LoginComponent.NO_LICENSE_ERROR_MESSAGE);
      return;
    }

    let brancheChanged = false;
    const currentBrancheInStorage =
      localStorage.getItem("current-branche") ?? "Elevator";
    if (currentBrancheInStorage) {
      brancheChanged =
        currentBrancheInStorage !== result.settings?.system?.general?.branche;
      localStorage.setItem(
        "current-branche",
        result.settings?.system?.general?.branche ?? "",
      );
    }
    if (brancheChanged) {
      // Reload the page, so that the correct translations are loaded for the new branche.
      // Inside setTimeout, so thath the this._router.navigateByUrl call can set the new route and url in the address bar so that
      // the location.reload() call will reload the correct url with the new route and not the login page again.
      setTimeout(() => {
        document.location.reload();
      }, 100);
    }

    if (this._userService.HasClaim(Claims.Settings_ApplicationSettingsRW)) {
      this._adminClient
        .applicationSettingsConfiguredCorrectly(undefined)
        .pipe(
          switchMap((valResult) => {
            if (!valResult || (valResult.properties?.length ?? 0) === 0) {
              return NEVER;
            }
            return this._dlgs.AskYesCancel(
              $localize`:@@Login_ApplicationSettings_NotConfigured_Content:There are some application settings that need to be configured.\r\nDo you want to configure them now?`,
              $localize`:@@Login_ApplicationSettings_NotConfigured_Title:Application Configuration Incomplete`,
              $localize`:@@Login_ApplicationSettings_NotConfigured_YesButton:Configure Now`,
              { id: "dlgAppSettingsConfigure", idYes: "btnConfigureNow" },
            );
          }),
        )
        .subscribe((x) => {
          if (x !== CstDialogResult.Yes) {
            return;
          }

          this._router.navigate([NavRoutes.ADMIN_SETTINGS_APPLICATIONSETTINGS]);
        });
    }
  }

  public GetPasswordForgotQueryParams() {
    const username = (this.loginForm.value.username as string).replace(
      "\\",
      "/",
    );
    if (!username || !username.includes("/")) {
      return null;
    }
    const parts = username.split("/");
    const domain = parts[0].length > 0 ? parts[0] : null;
    const email =
      parts.length > 1 && parts[1].length > 0 && REGEX_EMAIL.test(parts[1])
        ? parts[1]
        : null;
    return { domain, email };
  }

  private _SaveState() {
    localStorage.setItem("LoginUiState", JSON.stringify(this._state));
  }
}
